import { createElement } from 'react';

/**
 * The navigation icons: 18px line drawings on a 24 grid, 1.7 stroke,
 * `currentColor` — the same drawings as the desktop navigator (§1.5), which is
 * what makes a surface recognisable in the app and in the browser.
 */
type Part = [string, Record<string, string | number>];

const ICONS: Record<string, Part[]> = {
  dashboard: [
    ['rect', { x: 3.5, y: 4.5, width: 5, height: 15, rx: 1.2 }],
    ['rect', { x: 10, y: 4.5, width: 5, height: 9, rx: 1.2 }],
    ['rect', { x: 16.5, y: 4.5, width: 4, height: 12, rx: 1.2 }]
  ],
  plan: [
    ['circle', { cx: 8, cy: 12, r: 3.4 }],
    ['path', { d: 'M11.4 12H21M18 12v3M15 12v2.2' }]
  ],
  billing: [
    ['rect', { x: 5, y: 3.5, width: 14, height: 17, rx: 1.6 }],
    ['path', { d: 'M8.5 8h7M8.5 12h7M8.5 16h4' }]
  ],
  downloads: [
    ['path', { d: 'M12 4v9m0 0 3.4-3.4M12 13 8.6 9.6' }],
    ['path', { d: 'M4.5 16v2.5A1.5 1.5 0 0 0 6 20h12a1.5 1.5 0 0 0 1.5-1.5V16' }]
  ],
  org: [
    ['circle', { cx: 5.5, cy: 6, r: 2.4 }],
    ['circle', { cx: 18.5, cy: 18, r: 2.4 }],
    ['path', { d: 'M5.5 8.4v5.4A2.2 2.2 0 0 0 7.7 16h8.4' }]
  ],
  seats: [
    ['circle', { cx: 9, cy: 8, r: 2.6 }],
    ['path', { d: 'M4.5 18.5c0-2.5 2-4.3 4.5-4.3s4.5 1.8 4.5 4.3' }],
    ['circle', { cx: 17, cy: 9.5, r: 2.1 }],
    ['path', { d: 'M15.4 14.6c2.3 0 4.1 1.7 4.1 3.9' }]
  ],
  devices: [
    ['rect', { x: 3, y: 5, width: 12, height: 9, rx: 1.4 }],
    ['path', { d: 'M6 18h6' }],
    ['rect', { x: 16.5, y: 9.5, width: 4.5, height: 9, rx: 1.2 }]
  ],
  profile: [
    ['circle', { cx: 12, cy: 8, r: 3.2 }],
    ['path', { d: 'M5.5 19.5c0-3.2 2.9-5.4 6.5-5.4s6.5 2.2 6.5 5.4' }]
  ],
  adminOverview: [
    ['path', { d: 'M4 7h10M18 7h2M4 17h2M10 17h10' }],
    ['circle', { cx: 16, cy: 7, r: 2.2 }],
    ['circle', { cx: 8, cy: 17, r: 2.2 }]
  ],
  projects: [
    ['path', { d: 'M4 7.5A1.5 1.5 0 0 1 5.5 6H10l2 2h6.5A1.5 1.5 0 0 1 20 9.5v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z' }],
    ['path', { d: 'M8 13h8' }]
  ],
  settings: [
    ['path', { d: 'M4.5 7H13M17 7h2.5M4.5 12H7M11 12h8.5M4.5 17H15M19 17h.5' }],
    ['circle', { cx: 15, cy: 7, r: 2 }],
    ['circle', { cx: 9, cy: 12, r: 2 }],
    ['circle', { cx: 17, cy: 17, r: 2 }]
  ],
  audit: [
    ['rect', { x: 5, y: 3.5, width: 14, height: 17, rx: 1.6 }],
    ['path', { d: 'm8.5 9 1.6 1.6L13.5 7.2M8.5 14h7M8.5 17h4.5' }]
  ],
  runs: [
    ['path', { d: 'M4.5 6.5h15M4.5 12h9M4.5 17.5h6' }],
    ['path', { d: 'm16 14.2 4.2 3.1-4.2 3.1z' }]
  ],
  spend: [
    ['path', { d: 'M4.5 19.5h15' }],
    ['path', { d: 'M7 16.5v-4M11 16.5V8M15 16.5v-6M19 16.5V5' }]
  ],
  streams: [
    ['path', { d: 'M4.5 8c4 0 4-3 8-3s4 3 8 3M4.5 13c4 0 4-3 8-3s4 3 8 3M4.5 18c4 0 4-3 8-3s4 3 8 3' }]
  ],
  adminOrgs: [
    ['rect', { x: 3.5, y: 7.5, width: 8, height: 12, rx: 1.2 }],
    ['rect', { x: 14, y: 4.5, width: 6.5, height: 15, rx: 1.2 }],
    ['path', { d: 'M6 11h3M6 14.5h3M16.5 8h1.5M16.5 12h1.5' }]
  ]
};

export function NavIcon({ name, size = 17 }: { name: string; size?: number }): React.JSX.Element {
  const parts = ICONS[name] ?? ICONS.dashboard;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {parts.map(([tag, attrs], index) => createElement(tag, { key: index, ...attrs }))}
    </svg>
  );
}

/**
 * The chrome's own glyphs, which are not navigation: a chevron that says a row
 * opens, one that says a row leads somewhere, the padlock that marks a private
 * thing, a plus, and a close. They take `currentColor` and no colour of their
 * own, so a row decides its own state and they follow.
 */
function Glyph({
  size,
  width = 1.8,
  d,
  rotate
}: {
  size: number;
  width?: number;
  d: string;
  rotate?: number;
}): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block', transform: rotate ? `rotate(${rotate}deg)` : undefined, transition: 'transform .12s' }}
    >
      <path d={d} />
    </svg>
  );
}

export function ChevronDown({ size = 11, open = false }: { size?: number; open?: boolean }): React.JSX.Element {
  return <Glyph size={size} width={2.2} d="m6 9.5 6 6 6-6" rotate={open ? 180 : 0} />;
}

export function ChevronRight({ size = 13 }: { size?: number }): React.JSX.Element {
  return <Glyph size={size} width={2} d="m9.5 6 6 6-6 6" />;
}

export function PlusMark({ size = 14 }: { size?: number }): React.JSX.Element {
  return <Glyph size={size} d="M12 6v12M6 12h12" />;
}

export function CloseMark({ size = 13 }: { size?: number }): React.JSX.Element {
  return <Glyph size={size} width={1.7} d="M6.5 6.5l11 11M17.5 6.5l-11 11" />;
}

/** The check of a done step and of « ✓ Sent » (§37.5, §37.6). */
export function CheckMark({ size = 16 }: { size?: number }): React.JSX.Element {
  return <Glyph size={size} width={2} d="m5 12.5 4.5 4.5L19 7.5" />;
}

export function LockMark({ size = 12 }: { size?: number }): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <rect x="5" y="11" width="14" height="9" rx="1.6" />
      <path d="M8.5 11V8.4a3.5 3.5 0 0 1 7 0V11" />
    </svg>
  );
}
